/**
 * 节点对齐 / 分布 —— 纯几何，零依赖。
 *
 * 对齐用**选区的包围盒**（而不是第一个节点）—— 跟 Figma / Blender 一致：
 * 选一堆节点点「左对齐」，是让它们都贴到最左边那个的左边缘。
 *
 * 分布用**等间隙**而不是等中心距：节点宽度不同时，等中心距看起来仍然是歪的，
 * 等间隙才是视觉上均匀的那个。
 */

/**
 * 尺寸类操作（「排列 → 尺寸」那一组）。
 *
 * ⚠️ 和 `AlignMode` 分开，因为它们的**结果不是位置**：
 * `alignNodes` 返回坐标，这两个返回尺寸。混进一个联合类型会让
 * 「返回的东西到底是什么」变得要靠 mode 猜 —— 那正是这一轮在清理的坏味道。
 */
export type SizeMode = 'hsize' | 'vsize'

/** 等宽/等高至少要 2 个才谈得上「统一」 */
export function canResize(count: number, _mode: SizeMode): boolean {
  return count > 1
}

/**
 * 把选中节点的宽度（或高度）统一到**其中最大的那个**。
 *
 * ⚠️ 取最大值而不是第一个：取第一个的话，用户选中的第一个恰好是个窄节点时，
 * 其余节点会被**压窄**、内容被截断 —— 那是破坏性操作，不该由一个
 * 「对齐」按钮做出来。取最大值只会让东西变大。
 */
export function resizeNodes(
  boxes: readonly AlignBox[],
  mode: SizeMode,
): { id: string; width: number; height: number }[] {
  if (boxes.length < 2) return []
  const key = mode === 'hsize' ? 'width' : 'height'
  const target = Math.max(...boxes.map((b) => b[key]))
  return boxes.map((b) => ({
    id: b.id,
    width: mode === 'hsize' ? target : b.width,
    height: mode === 'vsize' ? target : b.height,
  }))
}

export type AlignMode =
  | 'left'
  | 'hcenter'
  | 'right'
  | 'top'
  | 'vcenter'
  | 'bottom'
  | 'hdistribute'
  | 'vdistribute'

export interface AlignBox {
  id: string
  x: number
  y: number
  width: number
  height: number
}

export interface AlignPosition {
  id: string
  x: number
  y: number
}

/** 分布至少需要 3 个才有意义（两端固定，中间才有东西可挪） */
export const MIN_DISTRIBUTE_COUNT = 3

export function alignNodes(
  boxes: readonly AlignBox[],
  mode: AlignMode,
): AlignPosition[] {
  if (boxes.length === 0) return []

  const left = Math.min(...boxes.map((b) => b.x))
  const right = Math.max(...boxes.map((b) => b.x + b.width))
  const top = Math.min(...boxes.map((b) => b.y))
  const bottom = Math.max(...boxes.map((b) => b.y + b.height))

  switch (mode) {
    case 'left':
      return boxes.map((b) => ({ id: b.id, x: left, y: b.y }))

    case 'right':
      return boxes.map((b) => ({ id: b.id, x: right - b.width, y: b.y }))

    case 'hcenter': {
      const center = (left + right) / 2
      return boxes.map((b) => ({ id: b.id, x: center - b.width / 2, y: b.y }))
    }

    case 'top':
      return boxes.map((b) => ({ id: b.id, x: b.x, y: top }))

    case 'bottom':
      return boxes.map((b) => ({ id: b.id, x: b.x, y: bottom - b.height }))

    case 'vcenter': {
      const center = (top + bottom) / 2
      return boxes.map((b) => ({ id: b.id, x: b.x, y: center - b.height / 2 }))
    }

    case 'hdistribute': {
      if (boxes.length < MIN_DISTRIBUTE_COUNT) {
        return boxes.map((b) => ({ id: b.id, x: b.x, y: b.y }))
      }
      const sorted = [...boxes].sort((a, b) => a.x - b.x)
      const first = sorted[0] as AlignBox
      const last = sorted[sorted.length - 1] as AlignBox
      const span = last.x + last.width - first.x
      const totalWidth = sorted.reduce((sum, b) => sum + b.width, 0)
      // 可以出现负间隙（重叠时）—— 和 Figma 一致，不夹紧
      const gap = (span - totalWidth) / (sorted.length - 1)

      let cursor = first.x
      const moved = new Map<string, number>()
      for (const box of sorted) {
        moved.set(box.id, cursor)
        cursor += box.width + gap
      }
      return boxes.map((b) => ({ id: b.id, x: moved.get(b.id) ?? b.x, y: b.y }))
    }

    case 'vdistribute': {
      if (boxes.length < MIN_DISTRIBUTE_COUNT) {
        return boxes.map((b) => ({ id: b.id, x: b.x, y: b.y }))
      }
      const sorted = [...boxes].sort((a, b) => a.y - b.y)
      const first = sorted[0] as AlignBox
      const last = sorted[sorted.length - 1] as AlignBox
      const span = last.y + last.height - first.y
      const totalHeight = sorted.reduce((sum, b) => sum + b.height, 0)
      const gap = (span - totalHeight) / (sorted.length - 1)

      let cursor = first.y
      const moved = new Map<string, number>()
      for (const box of sorted) {
        moved.set(box.id, cursor)
        cursor += box.height + gap
      }
      return boxes.map((b) => ({ id: b.id, x: b.x, y: moved.get(b.id) ?? b.y }))
    }

    default:
      return boxes.map((b) => ({ id: b.id, x: b.x, y: b.y }))
  }
}

/** 判断某个对齐方式对当前选择是否可用（决定菜单项灰不灰） */
export function canAlign(count: number, mode: AlignMode): boolean {
  if (count < 2) return false
  if (mode === 'hdistribute' || mode === 'vdistribute') {
    return count >= MIN_DISTRIBUTE_COUNT
  }
  return true
}
